import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  buildSiderMenuModel,
  getSectionKey,
  resolveAncestorKeys,
  resolveSelectedOptionKey,
} from "../utils/sider/siderMenuModel";

const useSiderMenuState = ({
  sections,
  isExpanded,
  pathname,
  selectedDropdownOption,
  selectedSiderTab,
  setIsDropdownOpen,
  setIsExpanded,
  setSelectedDropdownOption,
  setSelectedSiderTab,
  setWhichDropdownOpen,
  whichDropdownOpen,
  handleWhichDropdownOpen,
  navigate,
  autoOpenSelectedPath = true,
}) => {
  const temporarilyExpandedRef = useRef(false);
  const [openKeys, setOpenKeys] = useState(() =>
    isExpanded && whichDropdownOpen.length > 0
      ? whichDropdownOpen.map(getSectionKey)
      : [],
  );

  const handleCollapsedSectionClick = useCallback(
    (key, tabName) => {
      temporarilyExpandedRef.current = true;
      setOpenKeys([key]);
      setWhichDropdownOpen([tabName]);
      setIsDropdownOpen(true);
      setIsExpanded(true);
    },
    [setIsDropdownOpen, setIsExpanded, setWhichDropdownOpen],
  );

  const menuModel = useMemo(
    () =>
      buildSiderMenuModel({
        sections,
        isExpanded,
      }),
    [isExpanded, sections],
  );

  const selectedRootKey = selectedSiderTab
    ? getSectionKey(selectedSiderTab)
    : null;
  const selectedOptionKey = useMemo(
    () =>
      resolveSelectedOptionKey({
        optionByKey: menuModel.optionByKey,
        selectedSiderTab,
        selectedDropdownOption,
        pathname,
      }),
    [
      menuModel.optionByKey,
      pathname,
      selectedDropdownOption,
      selectedSiderTab,
    ],
  );
  const selectedAncestorKey = useMemo(
    () =>
      resolveAncestorKeys({
        selectedOptionKey,
        parentKeyByKey: menuModel.parentKeyByKey,
        selectedRootKey,
      }).join("|"),
    [menuModel.parentKeyByKey, selectedOptionKey, selectedRootKey],
  );
  const selectedRootHasChildren = selectedRootKey
    ? menuModel.sectionByKey.get(selectedRootKey)?.hasChildren === true
    : false;

  useEffect(() => {
    if (!isExpanded) {
      setOpenKeys([]);
    }
  }, [isExpanded]);

  useEffect(() => {
    if (
      !autoOpenSelectedPath ||
      !isExpanded ||
      !selectedRootKey ||
      !selectedRootHasChildren
    ) {
      return;
    }

    const keysToOpen = [
      selectedRootKey,
      ...(selectedAncestorKey ? selectedAncestorKey.split("|") : []),
    ];
    setOpenKeys((currentKeys) => [
      ...new Set([...currentKeys, ...keysToOpen]),
    ]);
    setWhichDropdownOpen((currentSections) =>
      currentSections.includes(selectedSiderTab)
        ? currentSections
        : [...currentSections, selectedSiderTab],
    );
    setIsDropdownOpen(true);
  }, [
    isExpanded,
    autoOpenSelectedPath,
    selectedAncestorKey,
    selectedRootHasChildren,
    selectedRootKey,
    selectedSiderTab,
    setIsDropdownOpen,
    setWhichDropdownOpen,
  ]);

  const handleMenuOpenChange = useCallback(
    (nextOpenKeys) => {
      if (!isExpanded) return;

      const requestedRootKeys = nextOpenKeys.filter((key) =>
        menuModel.rootKeys.has(key),
      );
      const openSectionNames = requestedRootKeys.map(
        (key) => menuModel.sectionByKey.get(key).tabName,
      );
      setOpenKeys(nextOpenKeys);
      setWhichDropdownOpen(openSectionNames);
      setIsDropdownOpen(openSectionNames.length > 0);
    },
    [
      isExpanded,
      menuModel.rootKeys,
      menuModel.sectionByKey,
      setIsDropdownOpen,
      setWhichDropdownOpen,
    ],
  );

  const collapse = useCallback(() => {
    temporarilyExpandedRef.current = false;
    setOpenKeys([]);
    setWhichDropdownOpen([]);
    setIsDropdownOpen(false);
    setIsExpanded(false);
  }, [setIsDropdownOpen, setIsExpanded, setWhichDropdownOpen]);

  const handleMenuClick = useCallback(
    ({ key }) => {
      const section = menuModel.sectionByKey.get(key);
      if (section) {
        if (section.hasChildren && !isExpanded) {
          handleCollapsedSectionClick(key, section.tabName);
        } else if (!section.hasChildren && section.route) {
          setSelectedDropdownOption(null);
          setSelectedSiderTab(section.tabName);
          setWhichDropdownOpen([section.tabName]);
          setIsDropdownOpen(false);
          navigate(section.route);
        } else if (!section.hasChildren) {
          setSelectedSiderTab(section.tabName);
          handleWhichDropdownOpen(section.tabName);
        }
        if (!section.hasChildren && temporarilyExpandedRef.current) {
          collapse();
        }
        return;
      }

      const entry = menuModel.optionByKey.get(key);
      if (!entry?.option.navigate) return;

      setSelectedSiderTab(entry.tabName);
      setSelectedDropdownOption(
        entry.option.labelKey ?? entry.option.label,
      );
      navigate(entry.option.navigate);
      if (temporarilyExpandedRef.current) {
        collapse();
      }
    },
    [
      collapse,
      handleCollapsedSectionClick,
      handleWhichDropdownOpen,
      isExpanded,
      menuModel.optionByKey,
      menuModel.sectionByKey,
      navigate,
      setIsDropdownOpen,
      setSelectedDropdownOption,
      setSelectedSiderTab,
      setWhichDropdownOpen,
    ],
  );

  const expand = useCallback(() => {
    temporarilyExpandedRef.current = false;
    setIsExpanded(true);
  }, [setIsExpanded]);

  return {
    collapse,
    expand,
    handleMenuClick,
    handleMenuOpenChange,
    items: menuModel.items,
    openKeys,
    selectedKeys: selectedRootKey
      ? [isExpanded ? selectedOptionKey ?? selectedRootKey : selectedRootKey]
      : [],
  };
};

export default useSiderMenuState;
